import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Typography } from 'antd'

import { LightThemeProvider } from '@qflow/theme'

import {
  OnlyDesignMatchesFilter,
  RemovedFilter
} from 'portal/components/Filters'
import { HorizontalDivider } from 'portal/components/Navigation/Main/Toolbar/Divider'
import { RecordsExport } from 'portal/components/RecordsExport/RecordsExport'
import { Records } from 'portal/components/RecordsModules'
import { TableSettingsButton } from 'portal/components/RecordsTable/TableSettingsButton'
import { Sidebar } from 'portal/components/Sidebar'

import { safeParseJSON } from 'portal/lib/functions'
import { Flex, SideBar, Spacer, Spinner } from 'portal/lib/primitives'
import { PageContainer } from 'portal/pages/PageContainer'
import { selectGlobalDate } from 'portal/store/global/selectors'
import { compose } from 'portal/utils/compose'
import { withMixpanel } from 'portal/utils/mixpanel'

const { Title } = Typography
const EV_RECORDS_OPEN = 'frontend_records_open'
const EV_RECORDS_GENERATE_REPORT = 'frontend_records_generate_report'

const Delivery = props => {
  const { isDesignMatchesV2Enabled } = props
  const [filters, setFilters] = useState({})
  const [showRemovedRecords, setShowRemovedRecords] = useState(
    localStorage.getItem('removed-filter') === 'true' ? true : false
  )
  const [showOnlyDesignMatchesButton, setShowOnlyDesignMatchesButton] =
    useState(false)
  const [onlyShowDesignMatches, setOnlyShowDesignMatches] = useState(
    localStorage.getItem('only-design-matches-filter') === 'true' ? true : false
  )

  useEffect(() => {
    // Add Event Listener for storage
    window.addEventListener('storage', e => {
      const parsedStoredColumns = safeParseJSON(
        localStorage.getItem('delivery-shownTableColumns'),
        []
      )
      const storedColumns = Array.isArray(parsedStoredColumns)
        ? parsedStoredColumns
        : []
      const storedColumnsContainsDesignMatches =
        storedColumns?.includes('productsWithDesignMatchesCount') || false
      const shouldShowOnlyDesignMatchesButton =
        storedColumnsContainsDesignMatches && !isDesignMatchesV2Enabled
      setShowOnlyDesignMatchesButton(shouldShowOnlyDesignMatchesButton)
      if (!storedColumnsContainsDesignMatches) {
        setOnlyShowDesignMatches(false)
        localStorage.setItem('only-design-matches-filter', 'false')
      }
    })

    window.dispatchEvent(new Event('storage'))

    // Remove Event Listener on cleanup
    return () => {
      window.removeEventListener('storage', () => {})
    }
  }, [isDesignMatchesV2Enabled])

  useEffect(() => {
    if (isDesignMatchesV2Enabled) {
      setShowOnlyDesignMatchesButton(false)
      setOnlyShowDesignMatches(false)
      localStorage.setItem('only-design-matches-filter', 'false')
    }
  }, [isDesignMatchesV2Enabled])

  const [sortById, setSortById] = useState(false)

  const trackRecordsOpen = useCallback(() => {
    const { track } = props

    track(EV_RECORDS_OPEN, {
      module_type: 'delivery',
      record_type: 'delivery'
    })
  }, [props])

  const trackDataExported = useCallback(
    (params = {}) => {
      const { track } = props

      track(EV_RECORDS_GENERATE_REPORT, {
        module_type: 'delivery',
        record_type: 'delivery',
        ...params
      })
    },
    [props]
  )

  useEffect(() => {
    trackRecordsOpen()
  }, [trackRecordsOpen])

  const onFiltersUpdated = f => {
    setFilters(f)
  }

  const onOnlyDesignMatchesToggle = newState => {
    setOnlyShowDesignMatches(newState === 'true' ? true : false)
  }

  const onRemovedFilterToggle = newState => {
    setShowRemovedRecords(newState === 'true' ? true : false)
  }

  const updateSortById = newState => {
    setSortById(newState)
  }

  const { isLoading, globalDateRange } = props

  return (
    <LightThemeProvider>
      <Sidebar />
      <PageContainer style={{ paddingTop: '1.5rem', paddingBottom: '0.5rem' }}>
        {isLoading && <Spinner />}
        {!isLoading && (
          <>
            <Flex.Row
              amount={0}
              style={{ width: '99%', alignItems: 'baseline' }}
            >
              <Title level={2} id="Heading" style={{ marginBottom: '0' }}>
                Delivery Records
              </Title>
              <Flex.By amount={1} />
              <SideBar.GlobalDatePicker />
            </Flex.Row>
            <HorizontalDivider style={{ width: '99%' }} />
            <Flex.Row amount={0} style={{ width: '99%' }}>
              <TableSettingsButton />
              <Spacer.Fine />
              <RemovedFilter onToggle={onRemovedFilterToggle} />
              {showOnlyDesignMatchesButton && !isDesignMatchesV2Enabled && (
                <>
                  <Spacer.Fine />
                  <OnlyDesignMatchesFilter
                    onToggle={onOnlyDesignMatchesToggle}
                  />
                </>
              )}
              <Flex.By amount={1} />
              <RecordsExport
                title={`Delivery_Export_${globalDateRange.startDate.format(
                  'YYYY-MM-DD'
                )}_${globalDateRange.endDate.format('YYYY-MM-DD')}.csv`}
                startDate={globalDateRange.startDate}
                endDate={globalDateRange.endDate}
                filters={filters}
                trackDataExported={trackDataExported}
              />
            </Flex.Row>
          </>
        )}

        <Records.Delivery
          startDate={globalDateRange.startDate}
          endDate={globalDateRange.endDate}
          onFiltersUpdated={onFiltersUpdated}
          onlyShowDesignMatches={
            isDesignMatchesV2Enabled ? false : onlyShowDesignMatches
          }
          showRemovedRecords={showRemovedRecords}
          sortById={sortById}
          updateSortById={updateSortById}
        />
      </PageContainer>
    </LightThemeProvider>
  )
}

Delivery.propTypes = {
  isLoading: PropTypes.bool,
  areModulesReady: PropTypes.bool,
  exportRecords: PropTypes.func,

  ...withMixpanel.withProps
}

const selectGlobalDateFunc = selectGlobalDate()
const mapStateToProps = state => {
  return {
    isLoading: state.projectDetails.isLoading,
    isDesignMatchesV2Enabled:
      state.projectDetails?.modules?.isDesignMatchesV2Enabled === true,
    globalDateRange: selectGlobalDateFunc(state)
  }
}

const ComposedDelivery = compose(
  connect(mapStateToProps),
  withMixpanel
)(Delivery)

export { ComposedDelivery as Delivery }
